/**
 * AD-LITE ARCHITECTURE - Qur'an+ / Tanzeen +
 * 
 * "Beyond Elegance" Brand-Safe Ad Implementation
 * Maximum 3 ads per session for free-tier users
 * 
 * ============================================
 * AD PLACEMENTS (Strictly Limited):
 * ============================================
 * 
 * 1. LAUNCH-GATE AD (1 per day)
 *    - Shows on 2ND app launch of the day (not first)
 *    - Type: App-Open or Interstitial
 *    - Allows clean initial entry experience
 * 
 * 2. TASK-COMPLETION AD (1 per session)
 *    - Triggers AFTER high-value action:
 *      a) 2nd Forensic Scan completed
 *      b) 10 minutes of Quran reading
 *    - Type: Interstitial or Rewarded
 * 
 * 3. EXIT/TRANSITION AD (1 per session)
 *    - Shows when leaving major modules:
 *      a) Mosque Finder → Home
 *      b) Halal Scanner → Home
 *      c) Prayer Times → Home
 *    - Type: Interstitial
 * 
 * ============================================
 * FREQUENCY CAP: 3 ADS MAX PER SESSION
 * ============================================
 * 
 * CONTENT RATING: G (General audiences only)
 * - Blocks: Alcohol, Gambling, Dating, Adult content
 * 
 * PLACEMENT RULES:
 * - Never overlap navigation buttons
 * - Never overlap Ustadh chat interface
 * - Never interrupt active reading/prayer
 */

import { Capacitor } from '@capacitor/core';

// ============================================
// AD UNIT IDS (Replace with production IDs)
// ============================================
export const AD_UNITS = {
  // Test IDs for development
  TEST_APP_OPEN: 'ca-app-pub-3940256099942544/3419835294',
  TEST_INTERSTITIAL: 'ca-app-pub-3940256099942544/1033173712',
  TEST_REWARDED: 'ca-app-pub-3940256099942544/5224354917',
  
  // Production IDs (replace with actual AdMob unit IDs)
  APP_OPEN_IOS: process.env.REACT_APP_ADMOB_APP_OPEN_IOS || 'ca-app-pub-XXXXX/YYYYY',
  APP_OPEN_ANDROID: process.env.REACT_APP_ADMOB_APP_OPEN_ANDROID || 'ca-app-pub-XXXXX/YYYYY',
  INTERSTITIAL_IOS: process.env.REACT_APP_ADMOB_INTERSTITIAL_IOS || 'ca-app-pub-XXXXX/YYYYY',
  INTERSTITIAL_ANDROID: process.env.REACT_APP_ADMOB_INTERSTITIAL_ANDROID || 'ca-app-pub-XXXXX/YYYYY',
  REWARDED_IOS: process.env.REACT_APP_ADMOB_REWARDED_IOS || 'ca-app-pub-XXXXX/YYYYY',
  REWARDED_ANDROID: process.env.REACT_APP_ADMOB_REWARDED_ANDROID || 'ca-app-pub-XXXXX/YYYYY',
};

// ============================================
// AD-LITE CONFIGURATION
// ============================================
export const AD_LITE_CONFIG = {
  // GLOBAL FREQUENCY CAP - Maximum ads per session
  MAX_ADS_PER_SESSION: 3,
  
  // Task completion thresholds
  FORENSIC_SCANS_FOR_AD: 2,        // Show ad after 2nd scan
  DUA_GENERATIONS_FOR_AD: 2,       // Show ad after 2nd Personal Du'a generation
  QURAN_READING_MINUTES_FOR_AD: 10, // Show ad after 10 mins reading
  
  // Content rating (G = General audiences)
  MAX_AD_CONTENT_RATING: 'G',
  
  // Enable test mode for development
  TEST_MODE: process.env.NODE_ENV === 'development',
  
  // Minimum time between any two ads (ms)
  MIN_AD_INTERVAL: 60000, // 1 minute
};

// ============================================
// SESSION STORAGE KEYS
// ============================================
const STORAGE_KEYS = {
  // Session-based (reset on page refresh)
  SESSION_AD_COUNT: 'quranium_session_ad_count',
  TASK_COMPLETION_AD_SHOWN: 'quranium_task_ad_shown',
  EXIT_AD_SHOWN: 'quranium_exit_ad_shown',
  FORENSIC_SCAN_COUNT: 'quranium_forensic_scan_count',
  DUA_GENERATION_COUNT: 'quranium_dua_gen_count',
  QURAN_READING_START: 'quranium_reading_start',
  LAST_AD_TIME: 'quranium_last_ad_time',
  
  // Day-based (persist in localStorage)
  LAUNCH_COUNT_TODAY: 'quranium_launch_count_today',
  LAST_LAUNCH_DATE: 'quranium_last_launch_date',
  LAUNCH_GATE_SHOWN: 'quranium_launch_gate_shown_today',
};

// Check if running in native Capacitor environment
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

// Get today's date key
const getTodayKey = () => new Date().toISOString().split('T')[0];

/**
 * AdLiteService - Singleton for managing brand-safe ads
 * Implements strict 3-ad-per-session frequency cap
 */
class AdLiteService {
  constructor() {
    this.initialized = false;
    this.admob = null;
    this.sessionAdCount = 0;
    this.listeners = new Set();
    this.adInProgress = false;
    
    // Load session state
    this.loadSessionState();
  }

  /**
   * Load session state from storage
   */
  loadSessionState() {
    try {
      const today = getTodayKey();
      const lastDate = localStorage.getItem(STORAGE_KEYS.LAST_LAUNCH_DATE);
      
      // Reset daily counters if new day
      if (lastDate !== today) {
        localStorage.setItem(STORAGE_KEYS.LAST_LAUNCH_DATE, today);
        localStorage.setItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY, '0');
        localStorage.setItem(STORAGE_KEYS.LAUNCH_GATE_SHOWN, 'false');
      }
      
      // Load session ad count (resets on page refresh/new session)
      this.sessionAdCount = parseInt(sessionStorage.getItem(STORAGE_KEYS.SESSION_AD_COUNT) || '0', 10);
    } catch (e) {
      console.log('[AdLite] Storage not available');
    }
  }

  /**
   * Initialize the ad service
   */
  async initialize() {
    if (this.initialized) return;

    try {
      if (isNativeApp()) {
        try {
          // Only try to load AdMob in native app context
          // This will be handled by the Capacitor plugin
          console.log('[AdLite] Native app detected, AdMob initialization skipped (handled by native)');
        } catch (e) {
          console.log('[AdLite] AdMob not available:', e.message);
        }
      }
      
      this.initialized = true;
      
      // Track app launch
      this.trackAppLaunch();
    } catch (error) {
      console.error('[AdLite] Initialization error:', error);
      this.initialized = true;
    }
  }

  /**
   * Track app launch for Launch-Gate ad logic
   * Uses localStorage to persist across sessions
   */
  trackAppLaunch() {
    try {
      const today = getTodayKey();
      const lastDate = localStorage.getItem(STORAGE_KEYS.LAST_LAUNCH_DATE);
      
      if (lastDate !== today) {
        // New day - reset counters
        localStorage.setItem(STORAGE_KEYS.LAST_LAUNCH_DATE, today);
        localStorage.setItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY, '1');
        localStorage.setItem(STORAGE_KEYS.LAUNCH_GATE_SHOWN, 'false');
        console.log('[AdLite] New day, launch count reset to 1');
      } else {
        // Same day - increment launch count
        const launchCount = parseInt(localStorage.getItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY) || '0', 10) + 1;
        localStorage.setItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY, launchCount.toString());
        console.log('[AdLite] Same day, launch count:', launchCount);
      }
    } catch (e) {
      console.log('[AdLite] Could not track launch');
    }
  }

  /**
   * Check if we can show an ad (respects frequency cap)
   */
  canShowAd() {
    // Check global session cap
    if (this.sessionAdCount >= AD_LITE_CONFIG.MAX_ADS_PER_SESSION) {
      console.log('[AdLite] Session cap reached (3 ads shown)');
      return false;
    }
    
    // Check if an ad is already in progress
    if (this.adInProgress) {
      console.log('[AdLite] Ad already in progress');
      return false;
    }
    
    // Check minimum interval between ads
    try {
      const lastAdTime = parseInt(sessionStorage.getItem(STORAGE_KEYS.LAST_AD_TIME) || '0', 10);
      const now = Date.now();
      if (now - lastAdTime < AD_LITE_CONFIG.MIN_AD_INTERVAL) {
        console.log('[AdLite] Too soon since last ad');
        return false;
      }
    } catch (e) {
      console.debug('[AdLite] Session storage read failed:', e.message);
    }
    
    return true;
  }

  /**
   * Record that an ad was shown
   */
  recordAdShown(adType) {
    this.sessionAdCount++;
    sessionStorage.setItem(STORAGE_KEYS.SESSION_AD_COUNT, this.sessionAdCount.toString());
    sessionStorage.setItem(STORAGE_KEYS.LAST_AD_TIME, Date.now().toString());
    console.log(`[AdLite] ${adType} ad shown. Session count: ${this.sessionAdCount}/${AD_LITE_CONFIG.MAX_ADS_PER_SESSION}`);
  }

  /**
   * ============================================
   * AD PLACEMENT 1: LAUNCH-GATE AD
   * ============================================
   * Shows on 2nd app launch of the day (not first)
   */
  async showLaunchGateAd() {
    if (!this.canShowAd()) return false;
    
    try {
      const launchCount = parseInt(localStorage.getItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY) || '1', 10);
      const alreadyShown = localStorage.getItem(STORAGE_KEYS.LAUNCH_GATE_SHOWN) === 'true';
      
      // Only show on 2nd+ launch, and only once per day
      if (launchCount < 2 || alreadyShown) {
        console.log('[AdLite] Launch-Gate: Not eligible (launch count:', launchCount, ', shown:', alreadyShown, ')');
        return false;
      }
      
      console.log('[AdLite] Showing Launch-Gate ad');
      this.adInProgress = true;
      
      if (isNativeApp() && this.admob) {
        const platform = getPlatform();
        const adId = AD_LITE_CONFIG.TEST_MODE
          ? AD_UNITS.TEST_INTERSTITIAL
          : (platform === 'ios' ? AD_UNITS.INTERSTITIAL_IOS : AD_UNITS.INTERSTITIAL_ANDROID);
        
        await this.admob.prepareInterstitial({
          adId,
          isTesting: AD_LITE_CONFIG.TEST_MODE,
          npa: true, // Non-personalized ads
        });
        
        await this.admob.showInterstitial();
      } else {
        // Web: Show placeholder
        this.notifyListeners('showLaunchGateAd');
      }
      
      // Mark as shown (in localStorage so it persists)
      localStorage.setItem(STORAGE_KEYS.LAUNCH_GATE_SHOWN, 'true');
      this.recordAdShown('Launch-Gate');
      this.adInProgress = false;
      
      return true;
    } catch (error) {
      console.error('[AdLite] Launch-Gate error:', error);
      this.adInProgress = false;
      return false;
    }
  }

  /**
   * ============================================
   * AD PLACEMENT 2: TASK-COMPLETION AD
   * ============================================
   * Shows after: 2nd Forensic Scan OR 10 min Quran reading
   */
  async showTaskCompletionAd(taskType) {
    if (!this.canShowAd()) return false;
    
    try {
      const alreadyShown = sessionStorage.getItem(STORAGE_KEYS.TASK_COMPLETION_AD_SHOWN) === 'true';
      if (alreadyShown) {
        console.log('[AdLite] Task-Completion: Already shown this session');
        return false;
      }
      
      console.log(`[AdLite] Showing Task-Completion ad (trigger: ${taskType})`);
      this.adInProgress = true;
      
      if (isNativeApp() && this.admob) {
        const platform = getPlatform();
        const adId = AD_LITE_CONFIG.TEST_MODE
          ? AD_UNITS.TEST_INTERSTITIAL
          : (platform === 'ios' ? AD_UNITS.INTERSTITIAL_IOS : AD_UNITS.INTERSTITIAL_ANDROID);
        
        await this.admob.prepareInterstitial({
          adId,
          isTesting: AD_LITE_CONFIG.TEST_MODE,
          npa: true,
        });
        
        await this.admob.showInterstitial();
      } else {
        // Web: Show placeholder
        this.notifyListeners('showTaskCompletionAd', { taskType });
      }
      
      // Mark as shown
      sessionStorage.setItem(STORAGE_KEYS.TASK_COMPLETION_AD_SHOWN, 'true');
      this.recordAdShown('Task-Completion');
      this.adInProgress = false;
      
      return true;
    } catch (error) {
      console.error('[AdLite] Task-Completion error:', error);
      this.adInProgress = false;
      return false;
    }
  }

  /**
   * Track Forensic Scan completion for Task-Completion ad
   */
  trackForensicScan() {
    try {
      const count = parseInt(sessionStorage.getItem(STORAGE_KEYS.FORENSIC_SCAN_COUNT) || '0', 10) + 1;
      sessionStorage.setItem(STORAGE_KEYS.FORENSIC_SCAN_COUNT, count.toString());
      
      // Trigger ad on 2nd scan
      if (count === AD_LITE_CONFIG.FORENSIC_SCANS_FOR_AD) {
        this.showTaskCompletionAd('forensic_scan');
      }
      
      return count;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Track Personal Du'a generation completion for Task-Completion ad.
   * Mirrors trackForensicScan(): free users see one interstitial after their
   * 2nd successful generation within a session, respecting all frequency
   * caps (canShowAd), TASK_COMPLETION_AD_SHOWN gate, MAX_ADS_PER_SESSION,
   * and MIN_AD_INTERVAL.
   */
  trackDuaGeneration() {
    try {
      const count = parseInt(sessionStorage.getItem(STORAGE_KEYS.DUA_GENERATION_COUNT) || '0', 10) + 1;
      sessionStorage.setItem(STORAGE_KEYS.DUA_GENERATION_COUNT, count.toString());
      
      // Trigger ad on the configured Nth generation (default 2nd)
      if (count === AD_LITE_CONFIG.DUA_GENERATIONS_FOR_AD) {
        this.showTaskCompletionAd('dua_generation');
      }
      
      return count;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Start tracking Quran reading time
   */
  startQuranReading() {
    try {
      if (!sessionStorage.getItem(STORAGE_KEYS.QURAN_READING_START)) {
        sessionStorage.setItem(STORAGE_KEYS.QURAN_READING_START, Date.now().toString());
      }
    } catch (e) {
      console.debug('[AdLite] Failed to start reading timer:', e.message);
    }
  }

  /**
   * Check and trigger ad if 10 minutes of reading completed
   */
  checkQuranReadingTime() {
    try {
      const startTime = parseInt(sessionStorage.getItem(STORAGE_KEYS.QURAN_READING_START) || '0', 10);
      if (!startTime) return false;
      
      const elapsed = (Date.now() - startTime) / 1000 / 60; // minutes
      
      if (elapsed >= AD_LITE_CONFIG.QURAN_READING_MINUTES_FOR_AD) {
        // Reset reading timer
        sessionStorage.removeItem(STORAGE_KEYS.QURAN_READING_START);
        this.showTaskCompletionAd('quran_reading_10min');
        return true;
      }
      
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * ============================================
   * AD PLACEMENT 3: EXIT/TRANSITION AD
   * ============================================
   * Shows when transitioning from major module to Home
   */
  async showExitTransitionAd(fromModule) {
    if (!this.canShowAd()) return false;
    
    try {
      const alreadyShown = sessionStorage.getItem(STORAGE_KEYS.EXIT_AD_SHOWN) === 'true';
      if (alreadyShown) {
        console.log('[AdLite] Exit-Transition: Already shown this session');
        return false;
      }
      
      // Only trigger from specific modules
      const validModules = ['mosque_finder', 'halal_scanner', 'prayer_times'];
      if (!validModules.includes(fromModule)) {
        console.log('[AdLite] Exit-Transition: Invalid module:', fromModule);
        return false;
      }
      
      console.log(`[AdLite] Showing Exit-Transition ad (from: ${fromModule})`);
      this.adInProgress = true;
      
      if (isNativeApp() && this.admob) {
        const platform = getPlatform();
        const adId = AD_LITE_CONFIG.TEST_MODE
          ? AD_UNITS.TEST_INTERSTITIAL
          : (platform === 'ios' ? AD_UNITS.INTERSTITIAL_IOS : AD_UNITS.INTERSTITIAL_ANDROID);
        
        await this.admob.prepareInterstitial({
          adId,
          isTesting: AD_LITE_CONFIG.TEST_MODE,
          npa: true,
        });
        
        await this.admob.showInterstitial();
      } else {
        // Web: Show placeholder
        this.notifyListeners('showExitTransitionAd', { fromModule });
      }
      
      // Mark as shown
      sessionStorage.setItem(STORAGE_KEYS.EXIT_AD_SHOWN, 'true');
      this.recordAdShown('Exit-Transition');
      this.adInProgress = false;
      
      return true;
    } catch (error) {
      console.error('[AdLite] Exit-Transition error:', error);
      this.adInProgress = false;
      return false;
    }
  }

  /**
   * Get current session ad stats
   */
  getAdStats() {
    return {
      sessionAdCount: this.sessionAdCount,
      maxAdsPerSession: AD_LITE_CONFIG.MAX_ADS_PER_SESSION,
      remainingAds: AD_LITE_CONFIG.MAX_ADS_PER_SESSION - this.sessionAdCount,
      launchGateShown: localStorage.getItem(STORAGE_KEYS.LAUNCH_GATE_SHOWN) === 'true',
      taskCompletionShown: sessionStorage.getItem(STORAGE_KEYS.TASK_COMPLETION_AD_SHOWN) === 'true',
      exitTransitionShown: sessionStorage.getItem(STORAGE_KEYS.EXIT_AD_SHOWN) === 'true',
      launchCountToday: parseInt(localStorage.getItem(STORAGE_KEYS.LAUNCH_COUNT_TODAY) || '1', 10),
    };
  }

  /**
   * Add listener for ad events (for web placeholder UI)
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(event, data = null) {
    this.listeners.forEach(callback => callback(event, data));
  }

  /**
   * Reset session (call when user upgrades to Pro)
   */
  resetSession() {
    this.sessionAdCount = 0;
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_AD_COUNT);
    sessionStorage.removeItem(STORAGE_KEYS.TASK_COMPLETION_AD_SHOWN);
    sessionStorage.removeItem(STORAGE_KEYS.EXIT_AD_SHOWN);
    sessionStorage.removeItem(STORAGE_KEYS.FORENSIC_SCAN_COUNT);
    sessionStorage.removeItem(STORAGE_KEYS.QURAN_READING_START);
    console.log('[AdLite] Session reset');
  }

  /**
   * Cleanup method (alias for resetSession, used when upgrading to Pro)
   */
  async cleanup() {
    this.resetSession();
  }
}

// Singleton instance
export const adLiteService = new AdLiteService();
export { AdLiteService };
